import React, { useState, useEffect, useMemo } from "react";
import { observer } from "mobx-react";
import { WIDGET_META, CATEGORY_LABEL as CAT_LABEL } from "../utils/widgetConstants";
import dashboardV2Store from "../store/dashboardV2Store";
import WidgetCatalogLoader from "./WidgetCatalogLoader";
import WidgetPreview from "./WidgetPreview";
import ConfigureWidgetPanel from "./ConfigureWidgetPanel";
import userDetail from "Stores/userDetail";

const WidgetCatalog = observer(() => {
  const [cat, setCat] = useState("All");
  const [search, setSearch] = useState("");
  const [hovId, setHovId] = useState(null);
  const [configuringWidget, setConfiguringWidget] = useState(null);

  const {
    activeDashboard = null,
    widgetCatalog = [],
    isLoadingCatalog = false,
    addingWidgetId = null
  } = dashboardV2Store;

  const addedWidgetIds = new Set(
    activeDashboard?.widgets?.map(w => w.widgetId) || []
  );

  const availableCats = useMemo(() => {
    const cats = new Set(widgetCatalog.map(w => w.category).filter(Boolean));
    return ["All", ...["EMAIL", "CALLING", "LINKEDIN", "TASKS", "OVERVIEW", "CONTACTS"].filter(c => cats.has(c))];
  }, [widgetCatalog]);

  const filtered = widgetCatalog.filter(w => {
    const matchesCat = cat === "All" || w.category === cat;
    const matchesSearch = (w.name || "").toLowerCase().includes(search.toLowerCase());
    return matchesCat && matchesSearch;
  });

  const hovWidget = hovId ? filtered.find(w => w.id === hovId) : null;
  const previewWidget = hovWidget || filtered[0] || null;
  const previewMeta = previewWidget ? WIDGET_META[previewWidget.id] : null;

  const handleAdd = widget => {
    if (!widget.id) return;
    // SG-2164: Configure step (Audience Scope) is shown for admins on EVERY widget — Audience Scope
    // applies to all widgets (graph or not), and the on-dashboard 3-dot menu already offers it for
    // every widget, so the Add-Widget popup must match. (It used to be gated on widgetHasGraph, a
    // leftover from when this panel also held Visualization Type; that was dropped, leaving only
    // Audience Scope, so the graph condition no longer belongs here.) The only thing it configures is
    // admin-only server-side (DashboardController.updateDashboard rejects audienceScope from non-admins
    // with 403), so non-admins always get the regular direct-add flow and never hit that 403.
    // NOTE: Utils.isAdmin() only checks team membership (true for nearly any user); isTeamOwner is
    // computed only for account-less users so it's false for real team admins (SG-2164 bug). Gate on
    // isTeamAdmin — the member's actual team role === ADMIN — which matches the backend's 403 guard.
    if (!!userDetail.userInfo.isTeamAdmin) {
      setConfiguringWidget(widget);
      return;
    }
    dashboardV2Store.addWidget(widget.id, widget.category);
  };

  const handleConfirmConfigured = async audienceScope => {
    if (!configuringWidget) return;
    await dashboardV2Store.addWidget(configuringWidget.id, configuringWidget.category, audienceScope);
    setConfiguringWidget(null);
  };

  const handleRemove = widget => {
    dashboardV2Store.removeWidget(widget.id);
  };

  useEffect(() => {
    dashboardV2Store.fetchWidgetCatalog();
  }, []);

  return (
    <div
      className="widgetCatalogOverlay"
      onClick={() => dashboardV2Store.setShowWidgetCatalog(false)}
    >
      <div
        className="widgetCatalogModal"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="widgetCatalogModalHeader">
          <div className="widgetCatalogModalHeaderTop">
            <div>
              <p className="widgetCatalogModalTitle">Add a widget</p>
              <p className="widgetCatalogModalSubtitle">
                Choose from our pre-built widgets. Each widget runs its own query.
              </p>
            </div>
            <button
              type="button"
              className="widgetCatalogCloseBtn"
              onClick={() => dashboardV2Store.setShowWidgetCatalog(false)}
            >
              ×
            </button>
          </div>
          <div className="widgetCatalogModalFilters">
            <input
              className="widgetCatalogSearch"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search widgets…"
            />
            <div className="widgetCatalogCatTabs">
              {availableCats.map(c => (
                <button
                  key={c}
                  type="button"
                  className={`widgetCatalogCatTab${cat === c ? " widgetCatalogCatTabActive" : ""}`}
                  onClick={() => setCat(c)}
                >
                  {c === "All" ? "All" : CAT_LABEL[c]}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="widgetCatalogModalBody">
          {/* Widget list */}
          <div className="widgetCatalogList">
            {isLoadingCatalog ? (
              <WidgetCatalogLoader />
            ) : filtered.length === 0 ? (
              <p className="widgetCatalogEmptyMsg">No widgets match &ldquo;{search}&rdquo;</p>
            ) : (
              <div className="widgetCatalogGrid">
                {filtered.map(widget => {
                  const widgetId = widget.id;
                  const isAdded = addedWidgetIds.has(widgetId);
                  const isHov = hovId === widgetId;
                  return (
                    <div
                      key={widgetId}
                      className={`widgetCatalogCard${isHov ? " widgetCatalogCardHov" : ""}`}
                      onMouseEnter={() => setHovId(widgetId)}
                      onMouseLeave={() => setHovId(null)}
                    >
                      <div className="widgetCatalogCardTop">
                        <span className="widgetCatalogCatBadge">
                          {CAT_LABEL[widget.category] || widget.category}
                        </span>
                      </div>
                      <p className="widgetCatalogCardTitle">{widget.name}</p>
                      {WIDGET_META[widgetId]?.metrics?.length > 0 && (
                        <div className="widgetCatalogMetricTags">
                          {WIDGET_META[widgetId].metrics.slice(0, 3).map(m => (
                            <span key={m} className="widgetCatalogMetricTag">{m}</span>
                          ))}
                          {WIDGET_META[widgetId].metrics.length > 3 && (
                            <span className="widgetCatalogMetricTagMore">+{WIDGET_META[widgetId].metrics.length - 3}</span>
                          )}
                        </div>
                      )}
                      {isAdded ? (
                        <div className="widgetCatalogCardActions">
                          <div className="widgetCatalogAddedBadge">✓ Added</div>
                          <button
                            type="button"
                            className="widgetCatalogRemoveBtn"
                            onClick={e => { e.stopPropagation(); handleRemove(widget); }}
                          >
                            Remove
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          className={`widgetCatalogAddBtn${isHov && !addingWidgetId ? " widgetCatalogAddBtnHov" : ""}${addingWidgetId === widgetId ? " widgetCatalogAddBtnLoading" : ""}`}
                          onClick={e => { e.stopPropagation(); handleAdd(widget); }}
                          disabled={!!addingWidgetId}
                        >
                          {addingWidgetId === widgetId ? "Adding…" : "+ Add widget"}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Preview panel — replaced by the Configure step for graph-capable widgets being added */}
          <div className="widgetCatalogPreview">
            {configuringWidget ? (
              <ConfigureWidgetPanel
                widget={configuringWidget}
                onBack={() => setConfiguringWidget(null)}
                onConfirm={handleConfirmConfigured}
                isAdding={addingWidgetId === configuringWidget.id}
              />
            ) : previewWidget ? (
              <>
                <p className="widgetCatalogPreviewLabel">Preview</p>
                <div className="widgetCatalogPreviewCard">
                  <div className="widgetCatalogPreviewCardHeader">
                    <p className="widgetCatalogPreviewTitle">{previewWidget.name}</p>
                    <span className="widgetCatalogCatBadge">{CAT_LABEL[previewWidget.category] || previewWidget.category}</span>
                  </div>
                  <div className="widgetCatalogPreviewContent">
                    <WidgetPreview widgetId={previewWidget.id} />
                  </div>
                </div>
                <p className="widgetCatalogPreviewHint">
                  Sample data shown for preview only.
                </p>
              </>
            ) : (
              <p className="widgetCatalogPreviewEmpty">Hover a widget to preview</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

export default WidgetCatalog;
